import { isAfter } from "date-fns";
import { TODO } from "@revelate/types";

type PropertyHistoryRecord = {
  timestamp?: string;
  value?: unknown;
  [key: string]: unknown;
};

function getRecordsAtSnapshot(
  records: unknown,
  snapshotTime: Date,
): PropertyHistoryRecord[] {
  if (!Array.isArray(records)) return [];

  return records
    .filter((record): record is PropertyHistoryRecord => {
      if (!record || typeof record !== "object" || !("timestamp" in record)) return false;
      const timestamp = new Date(String(record.timestamp));
      return !Number.isNaN(timestamp.getTime()) && !isAfter(timestamp, snapshotTime);
    })
    .toSorted((a, b) => (
      new Date(String(b.timestamp)).getTime() - new Date(String(a.timestamp)).getTime()
    ));
}

function parseHistoricalBoolean(value: unknown) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return null;
}

export function buildRiskHistoryDealSnapshot(
  deal: TODO,
  snapshotTime: Date,
) {
  const snapshotDeal = structuredClone(deal);
  const currentProperties = deal.provider_data?.properties ?? {};
  const completeHistories = deal.provider_data?.propertiesWithHistory ?? {};
  const historicalProperties = { ...structuredClone(currentProperties) };

  const prunedHistories = Object.fromEntries(
    Object.entries(completeHistories).map(([property, records]) => {
      const recordsAtSnapshot = getRecordsAtSnapshot(records, snapshotTime);

      if (recordsAtSnapshot.length > 0) {
        historicalProperties[property] = recordsAtSnapshot[0]?.value;
      } else {
        delete historicalProperties[property];
      }

      return [property, recordsAtSnapshot];
    }),
  );

  const historicalProbabilityValue = historicalProperties.hs_deal_stage_probability_shadow;
  const historicalProbability = Number(historicalProbabilityValue);
  const hasHistoricalProbability = Boolean(historicalProbabilityValue)
    && Number.isFinite(historicalProbability);
  const hasWonHistory = Object.hasOwn(completeHistories, "hs_is_closed_won");
  const hasLostHistory = Object.hasOwn(completeHistories, "hs_is_closed_lost");
  const historicalIsWon = hasWonHistory
    ? parseHistoricalBoolean(historicalProperties.hs_is_closed_won) ?? false
    : Boolean(deal.is_won);
  const historicalIsLost = hasLostHistory
    ? parseHistoricalBoolean(historicalProperties.hs_is_closed_lost) ?? false
    : Boolean(deal.is_lost);

  return {
    ...snapshotDeal,
    closed_at: historicalProperties.closedate ?? deal.closed_at,
    is_closed: hasWonHistory || hasLostHistory
      ? historicalIsWon || historicalIsLost
      : Boolean(deal.is_closed),
    is_won: historicalIsWon,
    is_lost: historicalIsLost,
    percentage_of_total_value: deal.deal_scale ?? deal.percentage_of_total_value ?? 10,
    close_probability: hasHistoricalProbability
      ? historicalProbability
      : deal.close_probability,
    stage_scale: hasHistoricalProbability
      ? historicalProbability.toFixed(2)
      : deal.stage_scale,
    risks: {},
    provider_data: {
      ...snapshotDeal.provider_data,
      properties: historicalProperties,
      propertiesWithHistory: prunedHistories,
    },
  };
}
